import React, { ChangeEvent, ForwardedRef, forwardRef, useRef, KeyboardEvent } from 'react';
import getclassNames from '../../utils/getClassNames';
import './Input.css';
import { IMaskInput } from 'react-imask';
import { format } from 'date-fns';
import { formatJSONDateWithoutTime } from '../../utils/dateHelpers';

export interface InputProps {
    type?: string;
    name?: string;
    id: string;
    onChange?: (value: string) => void;
    value?: string;
    label?: string;
    labelClassName?: string;
    inputClassName?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    maxLength?: number;
    mask?: string;
    useMaskedValue?: boolean;
    disableAutocomplete?: boolean;
    onKeyUp?: (key: string) => void;
}

const Input = forwardRef((props: InputProps, ref?: ForwardedRef<any>) => {
    const inputRef = useRef(null);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (props.onChange) {
            props.onChange(e.target.value);
        }
    }

    const handleOnKeyUp = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (props.onKeyUp) {
            props.onKeyUp(e.key)
        }
    }

    const directions = [];
    if (props.required) {
        directions.push('required');
    }

    if (props.type === 'number') {
        directions.push('numbers only');
    }

    if (props.type === "multi") {
        if (props.mask) {
            console.warn('Input mask not supported on multi-line inputs.  The mask will be ignored.');
        }

        return (<div className="input-container">
            {props.label && <label htmlFor={props.id} className={getclassNames('input-label', props.labelClassName || '')}>{props.label} {directions.length !== 0 && <span className="show-on-print text-italic">({directions.join(', ')})</span>}</label>}
            {props.required && <span className="margn-start-1 text-danger hide-on-print">*</span>}
            <div className="input textarea show-on-print">
                {props.value && <span dangerouslySetInnerHTML={{ __html: props.value.replace(/\n/g, '<br/>')}}></span>}
            </div>
            <textarea placeholder={props.placeholder} tabIndex={0}
                id={props.id}
                name={props.name || props.id}
                value={props.value}
                maxLength={props.maxLength}
                className={getclassNames('input textarea hide-on-print', props.inputClassName || '')}
                onChange={handleOnChange}
                disabled={props.disabled || false}
                autoComplete={props.disableAutocomplete ? 'off' : undefined}
                onKeyUp={handleOnKeyUp} />
        </div>);
    }

    if (props.mask) {
        const handleOnAccept = (value: any, _mask: IMask.InputMask<IMask.AnyMaskedOptions>) => {
            if (props.onChange) {
                props.onChange(value?.toString() || '');
            }
        };

        return (<div className="input-container">
            {props.label && <label htmlFor={props.id} className={getclassNames('input-label', props.labelClassName || '')}>{props.label} {directions.length !== 0 && <span className="show-on-print text-italic">({directions.join(', ')})</span>}</label>}
            {props.required && <span className="margin-start-1 text-danger hide-on-print">*</span>}
            {props.mask === 'PositiveDecimalNumber' && <IMaskInput
                mask={/(?:^\d+\.\d+$)|(?:^\d+\.?\d*$)/}
                value={props.value}
                unmask={true} // true|false|'typed'
                ref={ref}
                className={getclassNames('input', props.inputClassName || '')}
                inputRef={inputRef}  // access to nested input
                onAccept={handleOnAccept}
                placeholder={props.placeholder}
                disabled={props.disabled}
                autoComplete={props.disableAutocomplete ? 'off' : undefined}
                onKeyUp={handleOnKeyUp}
            />}
            {props.mask !== 'PositiveDecimalNumber' && <IMaskInput
                mask={props.mask}
                value={props.value}
                unmask={!props.useMaskedValue} // true|false|'typed'
                ref={ref}
                className={getclassNames('input', props.inputClassName || '')}
                inputRef={inputRef}  // access to nested input
                onAccept={handleOnAccept}
                onKeyUp={handleOnKeyUp}
                autoComplete={props.disableAutocomplete ? 'off' : undefined}
                placeholder={props.placeholder}
            />}
        </div>);
    }

    if (props.type === "date") {
        return (
            <div className="input-container">               
                {props.label && <label htmlFor={props.id} className={getclassNames('input-label', props.labelClassName || '')}>{props.label} {directions.length !== 0 && <span className="show-on-print text-italic">({directions.join(', ')})</span>}</label>}
                {props.required && <span className="margin-start-1 text-danger hide-on-print">*</span>}
                <div className="show-on-print">
                    <input placeholder={props.placeholder} ref={ref}
                        type="text"
                        value={props.value && formatJSONDateWithoutTime(props.value, 'MM/dd/yyyy')}
                        className={getclassNames('input', props.inputClassName || '')} />
                </div>
                <div className="hide-on-print">
                    <input placeholder={props.placeholder} ref={ref} tabIndex={0}
                        type="date"
                        id={props.id}
                        name={props.name || props.id}
                        value={props.value}
                        maxLength={props.maxLength}
                        className={getclassNames('input', props.inputClassName || '')}
                        onChange={handleOnChange}
                        onKeyUp={handleOnKeyUp}
                        autoComplete={props.disableAutocomplete ? 'off' : undefined}
                        disabled={props.disabled || false} />
                </div>
            </div>
        )
    }

    return (
        <div className="input-container">
            {props.label && <label htmlFor={props.id} className={getclassNames('input-label', props.labelClassName || '')}>{props.label} {directions.length !== 0 && <span className="show-on-print text-italic">({directions.join(', ')})</span>}</label>}
            {props.required && <span className="margin-start-1 text-danger hide-on-print">*</span>}
            <div className="input show-on-print">{props.value}</div>
            <input placeholder={props.placeholder} ref={ref} tabIndex={0}
                type={props.type || 'text'}
                id={props.id}
                name={props.name || props.id}
                value={props.value}
                maxLength={props.maxLength}
                className={getclassNames('input hide-on-print', props.inputClassName || '')}
                onChange={handleOnChange}
                onKeyUp={handleOnKeyUp}
                autoComplete={props.disableAutocomplete ? 'off' : undefined}
                disabled={props.disabled || false} />
        </div>
    )
});

export default Input;